import { useEffect, useMemo, useState } from 'react';
import {
  DialogContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LayersOutlined } from '@mui/icons-material';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  MolePrimaryButton,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { Util } from '../../util/Util';
import { Z_INDEX } from '../../styles/zIndex';
import type { VoucherRow } from './voucherTypes';
import { downloadTextFile, openPdfFromBase64 } from './voucherExport';

import { addMoney } from "../../util/quantity";
type Props = {
  isOpen: boolean;
  toggle: () => void;
  batch: string;
};

const VoucherBatchReport = ({ isOpen, toggle, batch }: Props) => {
  const { api } = useAuth();
  const { setLoadingMessage, queueReRun, t } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const [rows, setRows] = useState<VoucherRow[]>([]);
  const currency = appSettings?.BIZ_CURRENCY?.value || 'USD';

  const typeLabel = (value?: string) => {
    const key = String(value || '');
    const translated = t(`common:voucher.types.${key}`);
    return translated.startsWith('common:') ? key : translated;
  };

  const fetchData = async () => {
    if (!batch) return;
    const res = await api.get(`/vouchers/batch/${encodeURIComponent(batch)}`);
    if (res?.status === 200 && Array.isArray(res.data)) {
      setRows(res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else if (res?.status === 404) {
      toast.warn(t('common:toast.warn.noRecord'));
      toggle();
    }
  };

  useEffect(() => {
    if (isOpen && batch) {
      void fetchData();
    }
    return () => {
      if (!isOpen) setRows([]);
    };
  }, [isOpen, batch]);

  const total = useMemo(() => rows.reduce((sum, row) => addMoney(sum, Number(row.amount || 0)), 0), [rows]);
  const first = rows[0];

  const exportPdf = async () => {
    setLoadingMessage(t('common:report.loadingReport'));
    const res = await api.get(`/reports/vouchers/batch/${encodeURIComponent(batch)}`);
    setLoadingMessage(null);
    if (res?.status === 200 && res.data?.data) {
      openPdfFromBase64(res.data.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(exportPdf);
    } else {
      toast.error(t('common:report.failedToLoadReport'));
    }
  };

  const exportCsv = async () => {
    const res = await api.get(`/reports/vouchers/batch/${encodeURIComponent(batch)}/csv`, { responseType: 'text' } as any);
    if (res?.status === 200 && typeof res.data === 'string') {
      downloadTextFile(res.data, `vouchers.csv`);
    } else if (res?.status === 200 && res.data) {
      downloadTextFile(String(res.data), `vouchers.csv`);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(exportCsv);
    }
  };

  return (
    <MoleDialog
      open={isOpen}
      onClose={toggle}
      maxWidth="md"
      fullWidth
      themeColor={themeColor}
      paperOptions={{ maxHeight: '95vh' }}
      sx={{ zIndex: Z_INDEX.nestedDialog }}
    >
      <MoleDialogHeader
        title={t('common:voucher.batchReportTitle')}
        subtitle={t('common:voucher.batchReportHint')}
        icon={<LayersOutlined />}
        onClose={toggle}
        themeColor={themeColor}
      />
      <DialogContent sx={{ p: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
            <Typography variant="caption" color="text.secondary">{t('common:tables.count')}</Typography>
            <Typography variant="h6" fontWeight={800}>{rows.length}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
            <Typography variant="caption" color="text.secondary">{t('common:tables.amount')}</Typography>
            <Typography variant="h6" fontWeight={800}>{Util.formatCurrency(total, currency)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
            <Typography variant="caption" color="text.secondary">{t('common:voucher.issued')}</Typography>
            <Typography variant="h6" fontWeight={800}>
              {first?.issued ? Util.formatDate(first.issued, 'medium') : '—'}
            </Typography>
          </Paper>
        </Stack>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t('common:voucher.code')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common:voucher.status')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common:voucher.type')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common:voucher.issuee')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('common:tables.amount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{row.code}</TableCell>
                  <TableCell>{t(`common:voucher.statuses.${String(row.status || 'ACTIVE').toLowerCase()}`)}</TableCell>
                  <TableCell>{typeLabel(row.type)}</TableCell>
                  <TableCell>{row.issuee || '—'}</TableCell>
                  <TableCell align="right">{Util.formatNumber(Number(row.amount || 0))}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4}><Typography fontWeight={800}>{t('common:report.total')}</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight={800}>{Util.formatNumber(total)}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <MoleDialogActions>
        <MoleCancelButton onClick={toggle}>{t('common:ui.buttons.close')}</MoleCancelButton>
        <MolePrimaryButton color="inherit" variant="outlined" onClick={() => void exportCsv()}>
          {t('common:voucher.exportBatchCsv')}
        </MolePrimaryButton>
        <MolePrimaryButton startIcon={<FontAwesomeIcon icon={faFilePdf} />} onClick={() => void exportPdf()}>
          {t('common:voucher.exportBatchPdf')}
        </MolePrimaryButton>
      </MoleDialogActions>
    </MoleDialog>
  );
};

export default VoucherBatchReport;
